// Client-side service for the partner pairing flow (operator app side).
//
// `lookupPartnerCode` -- anon-callable preview. Identical "not claimable"
// shape across every failure mode so a caller cannot enumerate code
// state by timing or response shape.
//
// `redeemPartnerCode`  -- authenticated. Org owner enters the code on
// `/pair` (or it is passed through `core.rpc_bootstrap_org` via
// `bootstrapOrg({ referralCode })`).
//
// Both RPCs are defined in
// `supabase/migrations/20260605060100_partners_pairing_rpcs.sql`.

import { supabase } from '../client';
import { describeError } from '@pc/core';

export interface PartnerCodeLookupResult {
  claimable: boolean;
  partnerDisplayName?: string | null;
  expiresAt?: string | null;
  prospectEmailHint?: string | null;
  prospectNameHint?: string | null;
}

export async function lookupPartnerCode(
  code: string,
): Promise<PartnerCodeLookupResult> {
  // `app_public` is not in the generated `Database` typings (the codegen
  // only emits the canonical schemas), so cast through `any` to call the
  // anon-callable RPC. Mirrors the pattern used by `shopVendorClaim.ts`.
  const { data, error } = await (supabase() as any)
    .schema('app_public')
    .rpc('rpc_lookup_partner_code', { p_code: code });
  if (error) throw error;
  const row = (data ?? {}) as Record<string, unknown>;
  return {
    claimable: Boolean(row.claimable),
    partnerDisplayName: (row.partner_display_name as string | null) ?? null,
    expiresAt: (row.expires_at as string | null) ?? null,
    prospectEmailHint: (row.prospect_email_hint as string | null) ?? null,
    prospectNameHint: (row.prospect_name_hint as string | null) ?? null,
  };
}

export interface PartnerCodeRedeemSuccess {
  ok: true;
  referralId: string;
  partnerDisplayName: string;
  rateBps: number;
  appliesTo: 'plan' | 'plan_and_modules';
  reviewStatus: string;
}

export interface PartnerCodeRedeemFailure {
  ok: false;
  error: string;
  message: string;
}

export type PartnerCodeRedeemResult = PartnerCodeRedeemSuccess | PartnerCodeRedeemFailure;

export interface OrgReferralPairingStatus {
  paired: boolean;
  partnerDisplayName?: string;
}

export async function getOrgReferralPairing(
  orgId: string,
): Promise<OrgReferralPairingStatus> {
  const { data, error } = await (supabase() as any)
    .schema('app_public')
    .rpc('rpc_get_org_referral_pairing', { p_org_id: orgId });
  if (error) throw new Error(describeError(error));
  const row = (data ?? {}) as Record<string, unknown>;
  return {
    paired: Boolean(row.paired),
    partnerDisplayName: (row.partner_display_name as string | undefined) ?? undefined,
  };
}

export async function redeemPartnerCode(
  code: string,
  orgId: string,
): Promise<PartnerCodeRedeemResult> {
  // `app_public` is not in the generated `Database` typings; cast as any.
  const { data, error } = await (supabase() as any)
    .schema('app_public')
    .rpc('rpc_redeem_partner_code', { p_code: code, p_org_id: orgId });
  if (error) throw new Error(describeError(error));
  const row = (data ?? {}) as Record<string, unknown>;
  if (row.ok === true) {
    return {
      ok: true,
      referralId: row.referral_id as string,
      partnerDisplayName: (row.partner_display_name as string) ?? '',
      rateBps: Number(row.rate_bps ?? 0),
      appliesTo: (row.applies_to as 'plan' | 'plan_and_modules') ?? 'plan',
      reviewStatus: (row.review_status as string) ?? 'auto_approved',
    };
  }
  return {
    ok: false,
    error: (row.error as string) ?? 'unknown',
    message:
      typeof row.message === 'string'
        ? row.message
        : describeError(row.message ?? row),
  };
}
